/**
 * Hono Entity Routes.
 *
 * Mounts auto-generated CRUD + tree/dag + bulk + action routes on a
 * `Hono<NexusEnv>` router. Auth / optionalAuth middleware from
 * `ctx.core.middleware` is cast to Hono `MiddlewareHandler` at the mount site.
 */

import type { MiddlewareHandler } from 'hono'
import type {
  EntityDefinition,
  ModuleContext,
  CollectionEntityDefinition,
  SingleEntityDefinition,
  ActionDefinition
} from '@gzl10/nexus-sdk'
import type { HonoApp, NexusContext, NexusEnv } from '../types.js'
import { createHonoRouter } from '../types.js'
import type { EntityController, EntityHandler, EntityService } from '../controllers/types.js'
import { createActionHandler, ACTION_EXTRA_INPUT_KEY } from '../controllers/entity.controller.js'
import { streamSse, createBatchReporter } from '../sse.js'

/** Service interface for tree-like operations (tree and dag) */
interface TreeLikeService {
  getTree(rootId?: string, maxDepth?: number): Promise<unknown[]>
  findRoots(): Promise<unknown[]>
  findChildren(parentId: string | null): Promise<unknown[]>
  getAncestors(id: string): Promise<unknown[]>
  getDescendants(id: string): Promise<unknown[]>
  move(id: string, newParentId: string | null): Promise<unknown>
}

interface DagLikeService extends TreeLikeService {
  getParents(nodeId: string): Promise<unknown[]>
  setParents(nodeId: string, parentIds: string[]): Promise<void>
  addParent(nodeId: string, parentId: string): Promise<void>
  removeParent(nodeId: string, parentId: string): Promise<void>
}

type NexusMw = MiddlewareHandler<NexusEnv>

/**
 * Resolve the Express-era middleware registered under `ctx.core.middleware`.
 * The legacy registry is typed as Express `RequestHandler`. For Phase C we
 * cast to Hono's `MiddlewareHandler` — actual compatibility depends on the
 * middleware implementation (Phase D migrates auth/optionalAuth to Hono-native).
 *
 * TODO: Drop this cast once `ctx.core.middleware` holds Hono middleware.
 */
function toHonoMw(mw: unknown): NexusMw {
  return mw as NexusMw
}

/**
 * Create Hono router for an entity controller with optional auth middleware.
 */
export function createEntityRouter(
  controller: EntityController,
  definition: EntityDefinition,
  ctx: ModuleContext,
  service?: EntityService
): HonoApp {
  const router = createHonoRouter()

  const expose = 'expose' in definition ? (definition as { expose?: boolean }).expose : true
  if (expose === false) return router

  const type = definition.type ?? 'collection'
  const isSingleton = (type === 'single' || type === 'config') && !('scopeField' in definition && (definition as SingleEntityDefinition).scopeField)

  const entityDef = definition as CollectionEntityDefinition | SingleEntityDefinition
  const hasSkipAuthActions = entityDef.actions?.some(a => a.skipAuth)
  const hasWildcardPerms = !!definition.casl?.permissions?.['*']

  const coreAuth = ctx.core.middleware['auth']
  const coreOptionalAuth = ctx.core.middleware['optionalAuth']

  // Default-deny global auth (unless wildcard perms or selective per-route auth)
  if (!hasWildcardPerms && coreAuth && !hasSkipAuthActions) {
    router.use(toHonoMw(coreAuth))
  }

  // Wildcard perms: apply optionalAuth so anonymous users get abilities from '*' rules
  if (hasWildcardPerms && coreOptionalAuth) {
    router.use(toHonoMw(coreOptionalAuth))
  }

  // Per-route auth when skipAuth actions coexist
  const authMiddleware: NexusMw[] = !hasWildcardPerms && hasSkipAuthActions && coreAuth
    ? [toHonoMw(coreAuth)]
    : []

  // Entity-level middleware (e.g., rate limiting)
  const entityMiddleware: NexusMw[] = []
  if ('middleware' in definition && typeof definition.middleware === 'function') {
    const mw = definition.middleware(ctx)
    if (Array.isArray(mw)) {
      entityMiddleware.push(...mw.map(toHonoMw))
    } else {
      entityMiddleware.push(toHonoMw(mw))
    }
  }

  /**
   * Register a route with the given HTTP method + middleware chain + final handler.
   * Hono doesn't expose a generic method dispatcher, so switch explicitly.
   */
  function register(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    path: string,
    handlers: NexusMw[],
    handler: EntityHandler
  ): void {
    const all = [...handlers, async (c: NexusContext) => handler(c)] as NexusMw[]
    // Hono's path+handlers overloads don't narrow well with spread — use `.on()`.
    router.on(method, [path], ...all)
  }

  // Entity actions — registered BEFORE CRUD to avoid /:id capture
  const entityActions = entityDef.actions
  if (entityActions?.length) {
    for (const action of entityActions) {
      const rawScope = action.scope ?? 'row'
      if (rawScope === 'module') continue
      const scope = rawScope as 'row' | 'entity'
      const actionPath = scope === 'row' ? `/${action.key}/:id` : `/${action.key}`

      const handlers: NexusMw[] = [...entityMiddleware]

      if (!hasWildcardPerms && hasSkipAuthActions && !action.skipAuth && coreAuth) {
        handlers.push(toHonoMw(coreAuth))
      }
      if (action.skipAuth && coreOptionalAuth) {
        handlers.push(toHonoMw(coreOptionalAuth))
      }
      if (action.middleware) {
        const mw = action.middleware(ctx)
        if (Array.isArray(mw)) handlers.push(...mw.map(toHonoMw))
        else handlers.push(toHonoMw(mw))
      }

      const actionHandler = createActionHandler(action, entityDef, ctx, scope)
      const method = action.method ?? 'POST'
      register(method, actionPath, handlers, actionHandler)

      // SSE stream route for batch actions
      if (action.batch) {
        const streamPath = scope === 'row' ? `/${action.key}/stream/:id` : `/${action.key}/stream`
        const batchHandlers: NexusMw[] = [...entityMiddleware]

        if (!hasWildcardPerms && hasSkipAuthActions && !action.skipAuth && coreAuth) {
          batchHandlers.push(toHonoMw(coreAuth))
        }
        if (action.skipAuth && coreOptionalAuth) {
          batchHandlers.push(toHonoMw(coreOptionalAuth))
        }
        if (action.middleware) {
          const mw = action.middleware(ctx)
          if (Array.isArray(mw)) batchHandlers.push(...mw.map(toHonoMw))
          else batchHandlers.push(toHonoMw(mw))
        }

        register('GET', streamPath, batchHandlers, createBatchActionHandler(action, entityDef, ctx, scope))
      }
    }
  }

  // Tree/dag-specific routes (BEFORE /:id)
  if ((type === 'tree' || type === 'dag') && service) {
    const treeSvc = service as unknown as TreeLikeService

    register('GET', '/tree', [...entityMiddleware, ...authMiddleware], async (c) => {
      const rootId = c.req.query('rootId')
      const rawDepth = c.req.query('maxDepth')
      const maxDepth = rawDepth !== undefined ? parseInt(rawDepth, 10) : undefined
      return c.json(await treeSvc.getTree(rootId, Number.isNaN(maxDepth) ? undefined : maxDepth))
    })

    register('GET', '/roots', [...entityMiddleware, ...authMiddleware], async (c) => {
      return c.json(await treeSvc.findRoots())
    })

    register('POST', '/:id/move', [...entityMiddleware, ...authMiddleware], async (c) => {
      const id = String(c.req.param('id') ?? '')
      const body = await c.req.json<{ parentId: string | null }>().catch(() => ({ parentId: null }))
      return c.json(await treeSvc.move(id, body.parentId ?? null))
    })

    register('GET', '/:id/ancestors', [...entityMiddleware, ...authMiddleware], async (c) => {
      const id = String(c.req.param('id') ?? '')
      return c.json(await treeSvc.getAncestors(id))
    })

    register('GET', '/:id/descendants', [...entityMiddleware, ...authMiddleware], async (c) => {
      const id = String(c.req.param('id') ?? '')
      return c.json(await treeSvc.getDescendants(id))
    })

    register('GET', '/:id/children', [...entityMiddleware, ...authMiddleware], async (c) => {
      const id = String(c.req.param('id') ?? '')
      return c.json(await treeSvc.findChildren(id))
    })
  }

  // DAG-specific routes
  if (type === 'dag' && service) {
    const dagSvc = service as unknown as DagLikeService

    register('GET', '/:id/parents', [...entityMiddleware, ...authMiddleware], async (c) => {
      const id = String(c.req.param('id') ?? '')
      return c.json(await dagSvc.getParents(id))
    })

    register('PUT', '/:id/parents', [...entityMiddleware, ...authMiddleware], async (c) => {
      const id = String(c.req.param('id') ?? '')
      const body = await c.req.json<{ parentIds: string[] }>().catch(() => ({ parentIds: [] }))
      await dagSvc.setParents(id, body.parentIds)
      return c.body(null, 204)
    })

    register('POST', '/:id/parents', [...entityMiddleware, ...authMiddleware], async (c) => {
      const id = String(c.req.param('id') ?? '')
      const body = await c.req.json<{ parentId: string }>().catch(() => ({ parentId: '' }))
      await dagSvc.addParent(id, body.parentId)
      return c.body(null, 204)
    })

    register('DELETE', '/:id/parents/:parentId', [...entityMiddleware, ...authMiddleware], async (c) => {
      const id = String(c.req.param('id') ?? '')
      const parentId = String(c.req.param('parentId') ?? '')
      await dagSvc.removeParent(id, parentId)
      return c.body(null, 204)
    })
  }

  // Bulk routes (BEFORE /:id)
  if (controller.bulkCreate) {
    register('POST', '/bulk', [...entityMiddleware, ...authMiddleware], controller.bulkCreate)
  }
  if (controller.bulkUpdate) {
    register('PUT', '/bulk', [...entityMiddleware, ...authMiddleware], controller.bulkUpdate)
  }
  if (controller.bulkDelete) {
    register('DELETE', '/bulk', [...entityMiddleware, ...authMiddleware], controller.bulkDelete)
  }

  if (controller.count) {
    register('GET', '/count', [...entityMiddleware, ...authMiddleware], controller.count)
  }

  if (controller.recompute) {
    register('POST', '/recompute', [...entityMiddleware, ...authMiddleware], controller.recompute)
  }

  // List
  if (!isSingleton) {
    register('GET', '/', [...entityMiddleware, ...authMiddleware], controller.list)
  }

  // Get by ID (or / for singletons)
  if (isSingleton) {
    register('GET', '/', [...entityMiddleware, ...authMiddleware], controller.get)
  } else {
    register('GET', '/:id', [...entityMiddleware, ...authMiddleware], controller.get)
  }

  // Create
  if (controller.create) {
    register('POST', '/', [...entityMiddleware, ...authMiddleware], controller.create)
  }

  // Update
  if (controller.update) {
    if (isSingleton) {
      register('PUT', '/', [...entityMiddleware, ...authMiddleware], controller.update)
    } else {
      register('PUT', '/:id', [...entityMiddleware, ...authMiddleware], controller.update)
    }
  }

  // Delete
  if (controller.delete) {
    register('DELETE', '/:id', [...entityMiddleware, ...authMiddleware], controller.delete)
  }

  return router
}

/**
 * Create SSE stream handler for entity-level batch actions (row/entity scope).
 * Reuses auth/CASL/record loading logic from `createActionHandler` by injecting
 * `_batchReporter` via `c.set(ACTION_EXTRA_INPUT_KEY, ...)`.
 */
function createBatchActionHandler(
  action: ActionDefinition,
  definition: CollectionEntityDefinition | SingleEntityDefinition,
  ctx: ModuleContext,
  scope: 'row' | 'entity'
): EntityHandler {
  const baseHandler = createActionHandler(action, definition, ctx, scope)

  return async (c: NexusContext): Promise<Response> => {
    return streamSse(c, async (sender) => {
      const reporter = createBatchReporter(sender)
      // Inject _batchReporter so createActionHandler picks it up into extendedInput
      ;(c as unknown as { set: (k: string, v: unknown) => void }).set(ACTION_EXTRA_INPUT_KEY, {
        _batchReporter: reporter
      })
      try {
        // baseHandler returns a Response we don't use (SSE already streaming). The handler
        // may throw; we translate that into a batch error event.
        await baseHandler(c)
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unknown error'
        ctx.core.logger.error({ action: action.key, error: message }, 'Batch action failed')
        reporter.fail('BATCH_ERROR', message)
      }
    })
  }
}
